// src/components/GameRatingModal.tsx

import React, {
  useEffect,
  useState,
} from "react";

import API_URL from "../config/api";

import "./GameRatingModal.css";


// =====================================================
// TYPES
// =====================================================

interface Props {
  open: boolean;
  gameKey: string;
  gameName: string;
  onComplete: () => void;
}


// =====================================================
// GUEST ID
// =====================================================

const GUEST_ID_KEY =
  "parena_guest_id";


const getOrCreateGuestId =
  (): string => {

    const existing =
      localStorage.getItem(
        GUEST_ID_KEY
      );

    if (existing) {
      return existing;
    }


    // -------------------------------------------------
    // GENERATE UUID
    // -------------------------------------------------

    let uuid: string;


    if (
      typeof crypto !== "undefined" &&
      typeof crypto.randomUUID ===
        "function"
    ) {

      uuid =
        crypto.randomUUID();

    } else {

      // Fallback for older browsers.
      uuid =
        `${Date.now()}_${Math.random()
          .toString(36)
          .slice(2)}`;
    }


    const guestId =
      `parena_guest_${uuid}`;


    localStorage.setItem(
      GUEST_ID_KEY,
      guestId
    );


    return guestId;
  };


// =====================================================
// GAME RATING MODAL
// =====================================================

const GameRatingModal:
  React.FC<Props> = ({
    open,
    gameKey,
    gameName,
    onComplete,
  }) => {

  // ===================================================
  // STATE
  // ===================================================

  const [
    selectedRating,
    setSelectedRating,
  ] = useState(0);

  const [
    hoveredRating,
    setHoveredRating,
  ] = useState(0);

  const [
    submitting,
    setSubmitting,
  ] = useState(false);

  const [
    submitted,
    setSubmitted,
  ] = useState(false);

  const [
    error,
    setError,
  ] = useState("");


  // ===================================================
  // RESET WHEN OPENED
  // ===================================================

  useEffect(() => {

    if (!open) {
      return;
    }

    setSelectedRating(0);
    setHoveredRating(0);
    setSubmitting(false);
    setSubmitted(false);
    setError("");

  }, [
    open,
  ]);


  // ===================================================
  // ESCAPE KEY
  // ===================================================

  useEffect(() => {

    if (!open) {
      return;
    }


    const handleKeyDown = (
      event: KeyboardEvent
    ) => {

      if (
        event.key === "Escape" &&
        !submitting
      ) {

        onComplete();
      }
    };


    window.addEventListener(
      "keydown",
      handleKeyDown
    );


    return () => {

      window.removeEventListener(
        "keydown",
        handleKeyDown
      );
    };

  }, [
    open,
    submitting,
    onComplete,
  ]);


  // ===================================================
  // RATING LABEL
  // ===================================================

  const activeRating =
    hoveredRating ||
    selectedRating;


  const ratingLabel =
    activeRating === 1
      ? "Not for me"
      : activeRating === 2
        ? "Could be better"
        : activeRating === 3
          ? "Good game"
          : activeRating === 4
            ? "Great game"
            : activeRating === 5
              ? "Outstanding"
              : "Choose your rating";


  // ===================================================
  // SUBMIT
  // ===================================================

  const submitRating =
    async () => {

      if (
        selectedRating < 1 ||
        selectedRating > 5 ||
        submitting
      ) {
        return;
      }


      setSubmitting(true);
      setError("");


      try {

        const token =
          localStorage.getItem(
            "token"
          );


        const headers:
          Record<string, string> = {
            "Content-Type":
              "application/json",
          };


        if (token) {

          headers.Authorization =
            `Bearer ${token}`;
        }


        const body:
          {
            rating: number;
            guestId?: string;
          } = {
            rating:
              selectedRating,
          };


        /*
          Logged-in players are identified
          by their JWT.

          Guests use a persistent browser ID.
        */

        if (!token) {

          body.guestId =
            getOrCreateGuestId();
        }


        const response =
          await fetch(
            `${API_URL}/api/games/${encodeURIComponent(
              gameKey
            )}/rating`,
            {
              method: "POST",
              headers,
              body:
                JSON.stringify(
                  body
                ),
            }
          );


        const data =
          await response.json();


        if (!response.ok) {

          throw new Error(
            data.message ||
            "Unable to save your rating."
          );
        }


        // ---------------------------------------------
        // SUCCESS
        // ---------------------------------------------

        setSubmitted(true);


        window.setTimeout(
          () => {

            onComplete();

          },
          1200
        );

      } catch (submitError) {

        console.error(
          "Could not submit game rating:",
          submitError
        );


        setError(
          submitError instanceof Error
            ? submitError.message
            : "Unable to save your rating."
        );

      } finally {

        setSubmitting(false);
      }
    };


  // ===================================================
  // CLOSED
  // ===================================================

  if (!open) {
    return null;
  }


  // ===================================================
  // UI
  // ===================================================

  return (

    <div
      className="game-rating-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="game-rating-title"
    >

      {/* =========================================== */}
      {/* AMBIENT EFFECTS                             */}
      {/* =========================================== */}

      <div
        className="game-rating-glow game-rating-glow-one"
        aria-hidden="true"
      />

      <div
        className="game-rating-glow game-rating-glow-two"
        aria-hidden="true"
      />


      {/* =========================================== */}
      {/* CARD                                        */}
      {/* =========================================== */}

      <div className="game-rating-card">

        <div
          className="game-rating-card-shine"
          aria-hidden="true"
        />


        {/* ========================================= */}
        {/* TOP MARK                                  */}
        {/* ========================================= */}

        <div
          className="game-rating-emblem"
          aria-hidden="true"
        >
          ♠
        </div>


        <div className="game-rating-eyebrow">
          MATCH COMPLETE
        </div>


        {/* ========================================= */}
        {/* SUCCESS                                   */}
        {/* ========================================= */}

        {submitted ? (

          <div className="game-rating-success">

            <div
              className="game-rating-success-icon"
              aria-hidden="true"
            >
              ✓
            </div>

            <h2 id="game-rating-title">
              Rating received
            </h2>

            <p>
              Thank you for helping make{" "}
              <strong>
                {gameName}
              </strong>{" "}
              better.
            </p>

          </div>

        ) : (

          <>
            {/* ===================================== */}
            {/* TITLE                                 */}
            {/* ===================================== */}

            <h2
              id="game-rating-title"
              className="game-rating-title"
            >
              How was {gameName}?
            </h2>

            <p className="game-rating-description">
              Your rating helps shape the next
              experience in the arena.
            </p>


            {/* ===================================== */}
            {/* STARS                                 */}
            {/* ===================================== */}

            <div
              className="game-rating-stars"
              role="radiogroup"
              aria-label={`Rate ${gameName}`}
              onMouseLeave={() =>
                setHoveredRating(0)
              }
            >

              {[
                1,
                2,
                3,
                4,
                5,
              ].map(
                (star) => {

                  const active =
                    star <=
                    activeRating;


                  return (

                    <button
                      key={star}
                      type="button"
                      className={
                        active
                          ? "game-rating-star active"
                          : "game-rating-star"
                      }
                      role="radio"
                      aria-checked={
                        selectedRating ===
                        star
                      }
                      aria-label={`${star} ${
                        star === 1
                          ? "star"
                          : "stars"
                      }`}
                      disabled={
                        submitting
                      }
                      onMouseEnter={() =>
                        setHoveredRating(
                          star
                        )
                      }
                      onFocus={() =>
                        setHoveredRating(
                          star
                        )
                      }
                      onBlur={() =>
                        setHoveredRating(0)
                      }
                      onClick={() => {

                        setSelectedRating(
                          star
                        );

                        setHoveredRating(
                          0
                        );

                        setError("");
                      }}
                    >
                      ★
                    </button>

                  );
                }
              )}

            </div>


            {/* ===================================== */}
            {/* RATING LABEL                          */}
            {/* ===================================== */}

            <div
              className={
                activeRating
                  ? "game-rating-label selected"
                  : "game-rating-label"
              }
              aria-live="polite"
            >
              {ratingLabel}
            </div>


            {/* ===================================== */}
            {/* DIVIDER                               */}
            {/* ===================================== */}

            <div
              className="game-rating-divider"
              aria-hidden="true"
            >
              <span />
              <strong>◆</strong>
              <span />
            </div>


            {/* ===================================== */}
            {/* ERROR                                 */}
            {/* ===================================== */}

            {error && (

              <div
                className="game-rating-error"
                role="alert"
              >
                {error}
              </div>

            )}


            {/* ===================================== */}
            {/* ACTIONS                               */}
            {/* ===================================== */}

            <div className="game-rating-actions">

              <button
                type="button"
                className="game-rating-submit"
                disabled={
                  selectedRating === 0 ||
                  submitting
                }
                onClick={
                  submitRating
                }
              >
                {submitting
                  ? "Submitting..."
                  : selectedRating
                    ? `Rate ${selectedRating} ${
                        selectedRating === 1
                          ? "Star"
                          : "Stars"
                      }`
                    : "Select a Rating"}
              </button>


              <button
                type="button"
                className="game-rating-skip"
                disabled={
                  submitting
                }
                onClick={
                  onComplete
                }
              >
                Maybe Later
              </button>

            </div>


            {/* ===================================== */}
            {/* FOOTNOTE                              */}
            {/* ===================================== */}

            <div className="game-rating-footnote">
              No comments. Just your rating.
            </div>

          </>

        )}

      </div>

    </div>
  );
};


export default GameRatingModal;
